import { createContext, useContext, useState, useCallback } from 'react';

const LanguageContext = createContext(null);

const translations = {
  ru: {
    // Navigation
    'nav.myDay': 'Мой день',
    'nav.myOrders': 'Мои заказы',
    'nav.calendar': 'Календарь',
    'nav.time': 'Время',
    'nav.overview': 'Обзор',
    'nav.orders': 'Заказы',
    'nav.clients': 'Клиенты',
    'nav.workers': 'Работники',
    'nav.services': 'Услуги',
    'nav.reports': 'Отчёты',
    'nav.settings': 'Настройки',
    'nav.integrations': 'Интеграции',
    'nav.teamSchedule': 'Расписание',
    'nav.audit': 'Аудит',
    'nav.payments': 'Платежи',
    'nav.expenses': 'Расходы',
    'nav.chat': 'Внутренний чат',

    // Chat
    'chat.title': 'Внутренний чат',
    'chat.noConversations': 'Нет диалогов',
    'chat.selectConversation': 'Выберите диалог',
    'chat.startConversation': 'Напишите первое сообщение',
    'chat.typePlaceholder': 'Введите сообщение...',
    'chat.worker': 'Работник',
    'chat.manager': 'Менеджер',
    'chat.newChat': 'Новый диалог',
    'chat.selectRecipient': 'Выберите получателя',
    'chat.fromCRM': 'Из CRM',
    'chat.fromDevice': 'С устройства',
    'chat.crmFiles': 'Файлы CRM',
    'chat.searchFiles': 'Поиск по заказам и клиентам...',
    'chat.invoices': 'Инвойсы',
    'chat.orderPhotos': 'Фото заказов',
    'chat.orderDocs': 'Документы',
    'chat.sendToChat': 'Отправить в чат',
    'chat.selectWorker': 'Выберите работника',
    'chat.sendFailed': 'Не удалось отправить сообщение',
    'chat.fileTooLarge': 'Файл слишком большой (макс. 10MB)',

    // Roles
    'role.admin': 'Администратор',
    'role.manager': 'Менеджер',
    'role.worker': 'Работник',

    // Common
    'common.profile': 'Профиль',
    'common.settings': 'Настройки',
    'common.logout': 'Выйти',
    'common.save': 'Сохранить',
    'common.cancel': 'Отмена',
    'common.delete': 'Удалить',
    'common.edit': 'Редактировать',
    'common.create': 'Создать',
    'common.search': 'Поиск',
    'common.loading': 'Загрузка...',
    'common.noData': 'Нет данных',
    'common.today': 'Сегодня',
    'common.week': 'Неделя',
    'common.back': 'Назад',
    'common.close': 'Закрыть',
    'common.confirm': 'Подтвердить',
    'common.date': 'Дата',
    'common.navigation': 'Навигация',
    'common.error': 'Ошибка',
    'common.mainMenu': 'Главное меню приложения',
    'common.allClear': 'Все в порядке!',
    'common.free': 'Свободно',
    'common.language': 'Язык',
    'common.status': 'Статус',
    'common.actions': 'Действия',
    'common.loadFailed': 'Не удалось загрузить данные',
    'common.saveFailed': 'Ошибка сохранения',
    'common.saved': 'Сохранено',
    'common.exportFailed': 'Ошибка экспорта',
    'common.testFailed': 'Тест не пройден',

    // Currency
    'currency.symbol': '$',
    'currency.format': (v) => `$${Number(v).toLocaleString('en-US')}`,

    // Auth
    'auth.login': 'Войти',
    'auth.email': 'Электронная почта',
    'auth.password': 'Пароль',
    'auth.signIn': 'Вход в систему',
    'auth.welcome': 'Добро пожаловать в CGC-CRM',

    // Dashboard - Worker
    'worker.hello': (name) => `Привет, ${name}!`,
    'worker.readyToStart': 'Готов начать?',
    'worker.haveAGoodDay': 'Хорошего дня!',
    'worker.startDay': 'Начать рабочий день',
    'worker.endDay': 'Завершить',
    'worker.dayStarted': (time) => `День начат в ${time}`,
    'worker.dayEnded': 'День завершён',
    'worker.dayStartedMsg': 'Рабочий день начат!',
    'worker.dayEndedMsg': 'Рабочий день завершён!',
    'worker.ordersCount': 'Заказов',
    'worker.inProgress': 'В работе',
    'worker.completed': 'Выполнено',
    'worker.currentlyActive': 'Сейчас в работе',
    'worker.upcoming': (n) => `Предстоящие (${n})`,
    'worker.completedSection': (n) => `Выполнено (${n})`,
    'worker.noOrdersToday': 'Нет заказов на сегодня',
    'worker.ordersWillAppear': 'Заказы появятся после назначения',
    'worker.startWork': 'Начать работу',
    'worker.finishWork': 'Завершить работу',
    'worker.workStarted': 'Работа начата!',
    'worker.workFinished': 'Работа завершена!',
    'worker.requiresService': 'Требуется доп. сервис',
    'worker.alreadyMarked': 'Уже отмечено',
    'worker.requiresServiceLabel': 'Требуется доп. обслуживание',
    'worker.marked': 'Отмечено',
    'worker.addNote': 'Заметка',
    'worker.addNotePlaceholder': 'Добавить заметку...',
    'worker.noteAdded': 'Заметка добавлена',
    'worker.call': 'Звонок',
    'worker.sms': 'SMS',
    'worker.route': 'Маршрут',
    'worker.addressNotSet': 'Адрес не указан',
    'worker.amount': 'Сумма',

    // Dashboard - Manager
    'manager.welcome': (name) => `Добро пожаловать, ${name}!`,
    'manager.businessOverview': 'Обзор вашего бизнеса за сегодня',
    'manager.ordersToday': 'Заказов',
    'manager.todayLabel': 'Сегодня',
    'manager.doneLabel': 'Выполнено',
    'manager.inProgressLabel': 'В процессе',
    'manager.workersLabel': 'Работников',
    'manager.quickActions': 'Быстрые действия',
    'manager.newOrder': 'Новый заказ',
    'manager.clientsAction': 'Клиенты',
    'manager.openCalendar': 'Открыть календарь',
    'manager.needsAttention': 'Требует внимания',
    'manager.ordersInProgress': (n) => `${n} заказов в работе`,

    // Photos
    'photos.title': 'Фото',
    'photos.before': 'Фото ДО',
    'photos.after': 'Фото ПОСЛЕ',
    'photos.beforeLabel': (n) => `До (${n})`,
    'photos.afterLabel': (n) => `После (${n})`,
    'photos.otherLabel': (n) => `Другое (${n})`,
    'photos.noPhotos': 'Нет фото. Нажмите кнопку выше, чтобы добавить.',
    'photos.uploaded': 'Фото загружено!',
    'photos.uploadError': 'Ошибка загрузки',
    'photos.onlyImages': 'Только изображения',
    'photos.maxSize': 'Максимум 10 МБ',

    // Files
    'files.title': 'Файлы',
    'files.attach': 'Прикрепить файл',
    'files.uploaded': 'Файл загружен!',
    'files.deleted': 'Файл удалён',
    'files.uploadError': 'Ошибка загрузки',
    'files.deleteError': 'Ошибка удаления',
    'files.downloadError': 'Ошибка скачивания',

    // Calendar
    'calendar.title': 'Календарь',
    'calendar.selectWorker': 'Выберите работника для просмотра расписания',
    'calendar.noOrders': 'Нет заказов на этот день',
    'calendar.unscheduled': 'Без времени:',
    'calendar.orders': (n) => {
      if (n === 1) return '1 заказ';
      if (n < 5) return `${n} заказа`;
      return `${n} заказов`;
    },
    'calendar.filterWorker': 'Фильтр по работнику',
    'calendar.allWorkers': 'Все работники',
    'calendar.teamSchedule': 'Расписание команды',
    'calendar.workload': 'Загрузка',
    'calendar.workloadLow': 'Низкая',
    'calendar.workloadMedium': 'Средняя',
    'calendar.workloadHigh': 'Высокая',
    'calendar.timeBlocks': 'Блокировка времени',
    'calendar.timeBlocksDescription': 'Блокировка времени для работников',
    'calendar.activeBlocks': 'Активные блокировки',
    'calendar.noBlocks': 'Нет активных блокировок',
    'calendar.addBlock': 'Добавить блокировку',
    'calendar.blockAdded': 'Блокировка добавлена',
    'calendar.blockDeleted': 'Блокировка удалена',
    'calendar.blocked': 'Заблокировано',
    'calendar.blockTitle': 'Напр: Отпуск, Собрание',
    'calendar.reason': 'Причина',
    'calendar.blockReason': 'Укажите причину (необязательно)',
    'calendar.filterActive': 'Расписание работника',
    'calendar.addOrder': 'Добавить заказ',
    'calendar.clickToAdd': 'Добавить заказ',
    'calendar.reassign': 'Переназначить',
    'calendar.noWorkerAssigned': 'Работник не назначен',
    'calendar.orderMoved': 'Заказ перенесён',
    'calendar.moveError': 'Ошибка переноса',
    'calendar.dragHint': 'Перетащите заказ на другой день',
    'calendar.timeConflict': 'Конфликт времени',
    'calendar.dropBlocked': 'Перенос заблокирован',

    // Common
    'common.clear': 'Сбросить',
    'common.save': 'Сохранить',
    'common.cancel': 'Отмена',

    // Statuses
    'status.new': 'Новый',
    'status.confirmed': 'Подтверждён',
    'status.in_progress': 'В работе',
    'status.done': 'Выполнен',
    'status.cancelled': 'Отменён',
    'status.deleted': 'Удалён',

    // Payment statuses
    'status.unpaid': 'Не оплачен',
    'status.partial': 'Частично',
    'status.paid': 'Оплачен',
    'status.refund': 'Возврат',
    
    // Status section titles
    'status.operationalTitle': 'Статус работы',
    'status.paymentTitle': 'Статус оплаты',

    // Priority
    'priority.low': 'Низкий',
    'priority.normal': 'Обычный',
    'priority.high': 'Высокий',
    'priority.urgent': 'Срочный',

    // Order statuses - action
    'action.confirm': 'Подтвердить',
    'action.startWork': 'Начать работу',
    'action.complete': 'Завершить',
    'action.openOrder': 'Открыть заказ',

    // Services
    'services.title': 'Типы услуг',
    'services.newService': 'Новая услуга',
    'services.name': 'Название',
    'services.description': 'Описание',
    'services.price': 'Цена',
    'services.duration': 'Длительность (мин)',
    'services.color': 'Цвет',
    'services.active': 'Активна',

    // Clients
    'clients.title': 'Клиенты',
    'clients.subtitle': 'База клиентов компании',
    'clients.addClient': 'Добавить клиента',
    'clients.editClient': 'Редактировать клиента',
    'clients.name': 'Название / ФИО',
    'clients.namePlaceholder': 'ООО Чистый Дом или Иванов Иван',
    'clients.phone': 'Телефон',
    'clients.phoneSecondary': 'Доп. телефон',
    'clients.email': 'Email',
    'clients.address': 'Адрес',
    'clients.street': 'Улица, дом',
    'clients.city': 'Город',
    'clients.postal': 'Индекс',
    'clients.tags': 'Теги (через запятую)',
    'clients.tagsPlaceholder': 'vip, уборка, постоянный',
    'clients.pinnedNote': 'Важная заметка (всегда видна)',
    'clients.pinnedNotePlaceholder': 'Важная информация о клиенте...',
    'clients.notes': 'Заметки',
    'clients.notesPlaceholder': 'Дополнительная информация...',
    'clients.contacts': 'Контакты',
    'clients.primary': 'Основной',
    'clients.stats': 'Статистика',
    'clients.ordersCount': 'Заказов',
    'clients.balance': 'Баланс',
    'clients.createOrder': 'Создать заказ',
    'clients.notFound': 'Клиенты не найдены',
    'clients.noClients': 'Нет клиентов',
    'clients.addFirst': 'Добавить первого клиента',
    'clients.inactive': 'Неактивен',
    'clients.deactivateConfirm': 'Вы уверены, что хотите деактивировать этого клиента?',
    'clients.openCard': 'Открыть карточку',
    'clients.searchPlaceholder': 'Поиск по имени, email, телефону, адресу...',
    'clients.portalLinkCreated': 'Ссылка на портал создана',
    'clients.portalLinkFailed': 'Не удалось создать ссылку',
    'clients.linkCopied': 'Ссылка скопирована',

    // Workers
    'workers.title': 'Работники',
    'workers.subtitle': 'Команда сотрудников',
    'workers.addWorker': 'Добавить работника',
    'workers.editWorker': 'Редактировать работника',
    'workers.firstName': 'Имя',
    'workers.lastName': 'Фамилия',
    'workers.email': 'Email',
    'workers.password': 'Пароль',
    'workers.phone': 'Телефон',
    'workers.color': 'Цвет в календаре',
    'workers.worker': 'Работник',
    'workers.skills': 'Навыки (через запятую)',
    'workers.skillsPlaceholder': 'уборка, мойка окон, химчистка',
    'workers.hourlyRate': 'Ставка в час',
    'workers.paymentType': 'Тип оплаты',
    'workers.hourlyPayment': 'Почасовая',
    'workers.hourlyPaymentDesc': 'Оплата за отработанные часы',
    'workers.percentagePayment': 'Процентная',
    'workers.percentagePaymentDesc': 'Процент от выручки',
    'workers.percentageRate': 'Процент от выручки',
    'workers.percentageNote': 'Зарплата = % от недельной выручки работника',
    'workers.percentageFromRevenue': 'от недельной выручки',
    'workers.noRateSet': 'Ставка не указана',
    'workers.active': 'Активен',
    'workers.inactive': 'Неактивен',
    'workers.noInactiveWorkers': 'Нет неактивных работников',
    'workers.restore': 'Восстановить',
    'workers.activate': 'Активировать',
    'workers.deactivate': 'Деактивировать',
    'workers.activateConfirm': 'Активировать работника?',
    'workers.deactivateConfirm': 'Деактивировать работника?',
    'workers.noWorkers': 'Нет работников',
    'workers.addFirst': 'Добавить первого работника',
    'workers.searchPlaceholder': 'Поиск работников...',
    'workers.availability': 'Доступность',
    'workers.currentSchedule': 'Текущее расписание',
    'workers.noAvailability': 'Расписание не настроено',
    'workers.addAvailability': 'Добавить время',
    'workers.available': 'Доступен',
    'workers.unavailable': 'Недоступен',
    'workers.delete': 'Удалить',
    'workers.deleteConfirm': 'Удалить работника?',
    'workers.deleteWarning': (name) => `Вы уверены, что хотите удалить работника ${name}? Это действие нельзя отменить.`,
    'workers.deactivated': 'Работник перемещён в неактивные',
    'workers.restored': 'Работник восстановлен',
    'workers.deactivateFailed': 'Не удалось деактивировать работника',
    'workers.restoreFailed': 'Не удалось восстановить работника',
    'workers.updated': 'Данные работника обновлены',
    'workers.updateFailed': 'Не удалось обновить данные',
    'workers.editWorker': 'Редактировать работника',
    'workers.editWorkerDesc': 'Измените данные работника',
    'workers.viewProfile': 'Папка работника',
    'workers.cinNumber': 'CIN номер',
    'workers.homeAddress': 'Домашний адрес',
    'workers.homeAddressPlaceholder': 'ул. Примерная, д. 1',
    'workers.workingDays': 'Рабочие дни',
    'workers.saturday': 'Суббота',
    'workers.sunday': 'Воскресенье',
    'workers.weekendHint': 'Автоматическое планирование включит выходные только для отмеченных дней',
    'workers.specialization': 'Специализация',
    'workers.specializationPlaceholder': 'Например: Электрик, Сантехник',
    'workers.contactInfo': 'Контактная информация',
    'workers.tapToCall': 'Нажмите для звонка',
    'workers.salary': 'Заработная плата',
    'workers.credentials': 'Данные для входа',
    'workers.login': 'Логин (Email)',
    'workers.password': 'Пароль',
    'workers.newPassword': 'Новый пароль',
    'workers.enterNewPassword': 'Введите новый пароль',
    'workers.passwordChanged': 'Пароль изменён',
    'workers.passwordChangeFailed': 'Не удалось изменить пароль',
    'workers.passwordTooShort': 'Пароль слишком короткий (мин. 4 символа)',
    'workers.show': 'Показать',
    'workers.hide': 'Скрыть',
    'workers.change': 'Изменить',
    'workers.passwordNote': 'Пароль был задан при создании работника',
    'workers.leaveBlankToKeep': 'Оставьте пустым, чтобы не менять пароль',
    'workers.role': 'Роль',
    'workers.roleAdmin': 'Админ',
    'workers.roleWorker': 'Работник',
    'workers.roleWorkerDesc': 'Выполняет заказы',
    'workers.roleManager': 'Менеджер',
    'workers.roleManagerDesc': 'Управляет заказами и клиентами',
    'workers.files': 'Файлы и документы',
    'workers.uploadFile': 'Загрузить файл',
    'workers.noFiles': 'Нет загруженных файлов',
    'workers.fileUploaded': 'Файл загружен',
    'workers.fileDeleted': 'Файл удалён',
    'workers.fileUploadError': 'Ошибка загрузки файла',
    'workers.fileDeleteError': 'Ошибка удаления файла',

    // Common - additions
    'common.day': 'День',
    'common.startTime': 'Начало',
    'common.endTime': 'Конец',
    'common.add': 'Добавить',
    'common.all': 'Все',
    'common.from': 'От',
    'common.to': 'До',
    'common.filter': 'Фильтр',
    'common.actions': 'Действия',

    // Login
    'login.title': 'Вход в систему',
    'login.subtitle': 'Введите данные для входа',
    'login.email': 'Электронная почта',
    'login.emailOrUsername': 'Логин или Email',
    'login.emailOrUsernamePlaceholder': 'Введите логин или email',
    'login.password': 'Пароль',
    'login.signIn': 'Войти',
    'login.invalidCredentials': 'Неверный логин или пароль. Попробуйте ещё раз.',
    'login.welcome': 'Добро пожаловать в CGC-CRM',
    'login.description': 'Система управления полевыми командами',

    // Orders
    'orders.title': 'Заказы',
    'orders.subtitle': 'Управление заказами и назначениями',
    'orders.newOrder': 'Новый заказ',
    'orders.client': 'Клиент',
    'orders.assignedTo': 'Назначен:',
    'orders.noDate': 'Дата не указана',
    'orders.updated': 'Заказ обновлён',
    'orders.updateFailed': 'Не удалось обновить заказ',
    'orders.deleted': 'Заказ удалён',
    'orders.deleteFailed': 'Не удалось удалить заказ',
    'orders.cannotDeleteCompleted': 'Нельзя удалить выполненный заказ',
    'orders.deleteConfirm': (title) => `Удалить заказ "${title}"?`,
    'orders.searchPlaceholder': 'Поиск по названию...',
    'orders.allStatuses': 'Все статусы',
    'orders.allPayments': 'Все оплаты',
    'orders.notFound': 'Заказы не найдены',
    'orders.noOrders': 'Нет заказов',
    'orders.date': 'Дата',
    'orders.workers': 'Работники',
    'orders.total': 'Сумма',
    'orders.paid': 'Оплачено',
    'orders.orderTotal': 'Сумма заказа',
    'orders.notAssigned': 'не назначен',
    'orders.serviceRequired': 'Требуется сервис',

    // Notifications
    'notifications.title': 'Уведомления',
    'notifications.markAllRead': 'Прочитать все',
    'notifications.noNew': 'Нет новых уведомлений',

    // Weekdays
    'weekday.mon': 'Пн',
    'weekday.tue': 'Вт',
    'weekday.wed': 'Ср',
    'weekday.thu': 'Чт',
    'weekday.fri': 'Пт',
    'weekday.sat': 'Сб',
    'weekday.sun': 'Вс',
    'weekday.monday': 'Понедельник',
    'weekday.tuesday': 'Вторник',
    'weekday.wednesday': 'Среда',
    'weekday.thursday': 'Четверг',
    'weekday.friday': 'Пятница',
    'weekday.saturday': 'Суббота',
    'weekday.sunday': 'Воскресенье',

    // Time Tracking
    'time.title': 'Учёт времени',
    'time.pageTitle': 'Подтверждение времени',
    'time.pageSubtitle': 'Рабочее время сотрудников на проверку',
    'time.myTime': 'Моё время',
    'time.myTimeSubtitle': 'История рабочего времени',
    'time.pending': 'На проверке',
    'time.approved': 'Подтверждено',
    'time.rejected': 'Отклонено',
    'time.all': 'Все',
    'time.hours': 'ч',
    'time.hoursLabel': 'Часов',
    'time.date': 'Дата',
    'time.worker': 'Работник',
    'time.dayStart': 'Начало',
    'time.dayEnd': 'Конец',
    'time.totalHours': 'Часов',
    'time.ordersCompleted': 'Заказов',
    'time.status': 'Статус',
    'time.actions': 'Действия',
    'time.approve': 'Подтвердить',
    'time.reject': 'Отклонить',
    'time.approveConfirm': 'Подтвердить рабочее время?',
    'time.rejectReason': 'Причина отклонения',
    'time.rejectReasonPlaceholder': 'Укажите причину отклонения...',
    'time.approvedMsg': 'Время подтверждено',
    'time.rejectedMsg': 'Время отклонено',
    'time.noSubmissions': 'Нет записей времени для проверки',
    'time.summary': 'Сводка за период',
    'time.summaryWorker': (name) => `Сводка: ${name}`,
    'time.periodStart': 'Начало периода',
    'time.periodEnd': 'Конец периода',
    'time.approvedHours': 'Подтверждено',
    'time.pendingHours': 'На проверке',
    'time.rejectedHours': 'Отклонено',
    'time.totalDays': 'Дней',
    'time.reviewedBy': 'Проверил',
    'time.reviewedAt': 'Дата проверки',
    'time.rejectionReason': 'Причина',
    'time.export': 'Экспорт',
    'time.exportCSV': 'Скачать CSV',
    'time.exportPDF': 'Скачать PDF',
    'time.exportSuccess': 'Отчёт экспортирован',
    'time.auditHistory': 'История изменений',
    'time.auditHistoryDescription': 'История всех изменений статусов времени',
    'time.noAuditHistory': 'Нет записей в истории',
    'time.action.approve': 'Подтверждено',
    'time.action.reject': 'Отклонено',
    'time.action.modify': 'Изменено',
    'time.action.resubmit': 'Переотправлено',
    'time.by': 'Кем',

    // Payroll
    'payroll.title': 'Зарплата',
    'payroll.subtitle': 'Расчёт и выплаты сотрудникам',
    'payroll.newRun': 'Новый расчёт',
    'payroll.periodStart': 'Начало периода',
    'payroll.periodEnd': 'Конец периода',
    'payroll.createRun': 'Создать расчёт',
    'payroll.draft': 'Черновик',
    'payroll.approved': 'Утверждён',
    'payroll.paid': 'Оплачен',
    'payroll.totalAmount': 'Итого к выплате',
    'payroll.worker': 'Работник',
    'payroll.paymentType': 'Тип оплаты',
    'payroll.hourly': 'Почасовая',
    'payroll.fixed': 'Фиксированная',
    'payroll.perOrder': 'За заказ',
    'payroll.combined': 'Комбинированная',
    'payroll.hoursWorked': 'Часов',
    'payroll.hourlyRate': 'Ставка/час',
    'payroll.ordersCompleted': 'Заказов',
    'payroll.rate': 'Ставка',
    'payroll.perOrderRate': 'За заказ',
    'payroll.fixedSalary': 'Оклад',
    'payroll.bonus': 'Бонус',
    'payroll.penalty': 'Штраф',
    'payroll.gross': 'Начислено',
    'payroll.net': 'К выплате',
    'payroll.addBonus': 'Добавить бонус/штраф',
    'payroll.bonusNote': 'Причина бонуса',
    'payroll.penaltyNote': 'Причина штрафа',
    'payroll.approve': 'Утвердить',
    'payroll.markPaid': 'Отметить оплаченным',
    'payroll.delete': 'Удалить',
    'payroll.export': 'Экспорт CSV',
    'payroll.noRuns': 'Нет расчётов',
    'payroll.createFirst': 'Создайте первый расчёт зарплаты',
    'payroll.runCreated': 'Расчёт создан',
    'payroll.runApproved': 'Расчёт утверждён',
    'payroll.runPaid': 'Расчёт отмечен оплаченным',
    'payroll.runDeleted': 'Расчёт удалён',
    'payroll.bonusAdded': 'Бонус/штраф добавлен',

    // Reports
    'reports.title': 'Отчёты',
    'reports.subtitle': 'Аналитика и статистика',
    'reports.period': 'Период',
    'reports.overview': 'Обзор',
    'reports.orders': 'Заказы',
    'reports.revenue': 'Выручка',
    'reports.workers': 'Работники',
    'reports.services': 'Услуги',
    'reports.totalOrders': 'Всего заказов',
    'reports.completedOrders': 'Выполнено',
    'reports.cancelledOrders': 'Отменено',
    'reports.completionRate': 'Выполнение',
    'reports.totalRevenue': 'Выручка',
    'reports.avgOrderValue': 'Средний чек',
    'reports.paidOrders': 'Оплачено',
    'reports.newClients': 'Новых клиентов',
    'reports.activeWorkers': 'Работников',
    'reports.totalHours': 'Часов',
    'reports.workerName': 'Работник',
    'reports.ordersCompleted': 'Выполнено',
    'reports.revenuePerHour': '$/час',
    'reports.hoursWorked': 'Часов',
    'reports.serviceName': 'Услуга',
    'reports.ordersCount': 'Заказов',
    'reports.export': 'Экспорт CSV',
    'reports.noData': 'Нет данных за период',
    'reports.byDate': 'По датам',
    'reports.byWorker': 'По работникам',
    'reports.byService': 'По услугам',
    'reports.byStatus': 'По статусам',
    'reports.workload': 'Загрузка',
    'reports.workloadComparison': 'Сравнение загрузки',
    'reports.scheduledHours': 'Запланировано',
    'reports.workedHours': 'Отработано',
    'reports.availableCapacity': 'Доступно',
    'reports.utilization': 'Загрузка',
    'reports.efficiency': 'Эффективность',
    'reports.hours': 'часов',
    'reports.exportPDF': 'Экспорт PDF',
    'reports.payroll': 'Зарплата',
    'reports.payrollTitle': 'Зарплатная ведомость',
    'reports.paymentType': 'Тип оплаты',
    'reports.rate': 'Ставка',
    'reports.hoursOrOrders': 'Часы/Заказы',
    'reports.ordersRevenue': 'Выручка',
    'reports.salary': 'Зарплата',
    'reports.totalSalary': 'Итого к выплате',
    'reports.workersCount': 'Работников',
    'reports.ordersShort': 'заказов',
    'reports.totalPayroll': 'Итого зарплата',

    // Scheduling
    'schedule.title': 'Расписание',
    'schedule.conflicts': 'Конфликты',
    'schedule.noConflicts': 'Нет конфликтов',
    'schedule.hasConflicts': 'Обнаружены конфликты',
    'schedule.conflictOverlap': 'Пересечение с другим заказом',
    'schedule.conflictUnavailable': 'Работник недоступен',
    'schedule.conflictBlocked': 'Время заблокировано',
    'schedule.availability': 'Доступность',
    'schedule.setAvailability': 'Установить доступность',
    'schedule.available': 'Доступен',
    'schedule.unavailable': 'Недоступен',
    'schedule.vacation': 'Отпуск',
    'schedule.sick': 'Больничный',
    'schedule.training': 'Обучение',
    'schedule.blocks': 'Блокировки времени',
    'schedule.addBlock': 'Добавить блокировку',
    'schedule.lunch': 'Обед',
    'schedule.meeting': 'Совещание',
    'schedule.personal': 'Личное',
    'schedule.travel': 'В пути',
    'schedule.buffer': 'Буфер между заказами',
    'schedule.bufferMinutes': 'минут',
    'schedule.settings': 'Настройки расписания',
    'schedule.workDayStart': 'Начало рабочего дня',
    'schedule.workDayEnd': 'Конец рабочего дня',

    // Audit
    'audit.title': 'Журнал аудита',
    'audit.subtitle': 'История всех действий в системе',
    'audit.accessDenied': 'Доступ запрещён',
    'audit.totalEvents': 'Всего событий',
    'audit.created': 'Создано',
    'audit.updated': 'Изменено',
    'audit.activeUsers': 'Активных пользователей',
    'audit.entityType': 'Тип объекта',
    'audit.action': 'Действие',
    'audit.events': 'События',
    'audit.noEvents': 'Нет событий за выбранный период',
    'audit.timestamp': 'Время',
    'audit.user': 'Пользователь',
    'audit.entity': 'Объект',
    'audit.eventDetails': 'Детали события',
    'audit.entityName': 'Название',
    'audit.details': 'Подробности',
    'audit.oldValue': 'Было',
    'audit.newValue': 'Стало',
    'audit.entity.order': 'Заказ',
    'audit.entity.client': 'Клиент',
    'audit.entity.worker': 'Работник',
    'audit.entity.user': 'Пользователь',
    'audit.entity.time': 'Время',
    'audit.entity.time_submission': 'Время',
    'audit.entity.document': 'Документ',
    'audit.entity.payroll': 'Зарплата',
    'audit.entity.scheduling': 'Планирование',
    'audit.action.create': 'Создание',
    'audit.action.update': 'Изменение',
    'audit.action.delete': 'Удаление',
    'audit.action.status': 'Смена статуса',
    'audit.action.status_change': 'Смена статуса',
    'audit.action.login': 'Вход',
    'audit.action.logout': 'Выход',
    'audit.action.assign': 'Назначение',
    'audit.action.approve': 'Подтверждение',
    'audit.action.reject': 'Отклонение',
    'audit.action.export': 'Экспорт',
    'audit.action.rate_change': 'Смена ставки',

    // Notification Settings
    'notifications.settingsTitle': 'Настройки уведомлений',
    'notifications.settingsSubtitle': 'Управление каналами уведомлений',
    'notifications.emailNotifications': 'Email уведомления',
    'notifications.emailDescription': 'Получать уведомления на email',
    'notifications.emailEnabled': 'Включить email',
    'notifications.emailEnabledDesc': 'Получать уведомления на электронную почту',
    'notifications.onAssignment': 'При назначении',
    'notifications.onAssignmentDesc': 'Когда вас назначают на заказ',
    'notifications.onStatusChange': 'При смене статуса',
    'notifications.onStatusChangeDesc': 'Когда меняется статус ваших заказов',
    'notifications.onTimeApproval': 'При подтверждении времени',
    'notifications.onTimeApprovalDesc': 'Когда ваше время подтверждено или отклонено',
    'notifications.pushNotifications': 'Push уведомления',
    'notifications.pushDescription': 'Мгновенные уведомления в браузере',
    'notifications.pushEnabled': 'Push включены',
    'notifications.pushEnabledDesc': 'Получать push уведомления',
    'notifications.enablePush': 'Включить',
    'notifications.pushNotSupported': 'Push уведомления не поддерживаются',
    'notifications.pushDenied': 'Разрешение на уведомления отклонено',
    'notifications.pushFailed': 'Не удалось включить push уведомления',
    'notifications.pushEnabled': 'Push уведомления включены',

    // Settings
    'settings.saved': 'Настройки сохранены',
    'settings.saveFailed': 'Не удалось сохранить',

    // Payments
    'payments.title': 'Платежи',
    'payments.subtitle': 'Управление платежами и счетами',
    'payments.accessDenied': 'Доступ запрещён',
    'payments.createPayment': 'Создать платёж',
    'payments.createDescription': 'Создать платёжное поручение для заказа',
    'payments.selectOrder': 'Выберите заказ',
    'payments.selectOrderPlaceholder': 'Выберите заказ...',
    'payments.amount': 'Сумма',
    'payments.provider': 'Провайдер',
    'payments.manual': 'Вручную',
    'payments.create': 'Создать',
    'payments.fillAllFields': 'Заполните все поля',
    'payments.created': 'Платёж создан',
    'payments.createFailed': 'Не удалось создать платёж',
    'payments.configured': 'Настроен',
    'payments.notConfigured': 'Не настроен',
    'payments.setupRequired': 'Требуется настройка платежей',
    'payments.setupInstructions': 'Добавьте ключи API в файл .env на сервере',
    'payments.stripeInstructions': 'STRIPE_SECRET_KEY и STRIPE_PUBLISHABLE_KEY',
    'payments.yookassaInstructions': 'YOOKASSA_SHOP_ID и YOOKASSA_SECRET_KEY',
    'payments.history': 'История платежей',
    'payments.noPayments': 'Нет платежей',
    'payments.date': 'Дата',
    'payments.order': 'Заказ',
    'payments.markPaid': 'Оплачен',
    'payments.markedPaid': 'Платёж отмечен как оплаченный',
    'payments.markPaidFailed': 'Не удалось отметить платёж',
    'payments.status.pending': 'Ожидает',
    'payments.status.succeeded': 'Оплачен',
    'payments.status.failed': 'Ошибка',
    'payments.status.refunded': 'Возврат',
    'payments.orderAmount': 'Сумма заказа',
    'payments.paid': 'Оплачено',
    'payments.paidInFull': 'Оплачен полностью',
    'payments.remaining': 'Остаток',
    'payments.pay': 'Оплатить',
    'payments.pendingPayments': 'Ожидают оплаты',
    'payments.more': 'ещё',
    'payments.enterAmount': 'Введите сумму',
    'payments.providerNotConfigured': 'Провайдер не настроен. Используйте ручную оплату.',
    'payments.emailReceipts': 'Email квитанции',
    'payments.emailSetup': 'Email для квитанций',

    // Reminders
    'reminders.sendReminder': 'Отправить напоминание',
    'reminders.remind': 'Напомнить',
    'reminders.type': 'Тип напоминания',
    'reminders.type.payment': 'Напоминание об оплате',
    'reminders.type.appointment': 'Напоминание о визите',
    'reminders.type.custom': 'Свой текст',
    'reminders.channel': 'Канал',
    'reminders.both': 'SMS и Email',
    'reminders.noPhone': 'нет телефона',
    'reminders.noEmail': 'нет email',
    'reminders.customMessage': 'Текст сообщения',
    'reminders.customPlaceholder': 'Введите текст напоминания...',
    'reminders.characters': 'символов',
    'reminders.preview': 'Предпросмотр',
    'reminders.send': 'Отправить',
    'reminders.sent': 'Напоминание отправлено',
    'reminders.failed': 'Ошибка отправки',
    'reminders.sentSuccess': 'Напоминание успешно отправлено!',
    'reminders.sentFailed': 'Не удалось отправить напоминание',

    // Client Portal
    'portal.title': 'Клиентский портал',
    'portal.generateLink': 'Создать ссылку на портал',
    'portal.copyLink': 'Копировать ссылку',
    'portal.linkCopied': 'Ссылка скопирована',
    'portal.linkGenerated': 'Ссылка создана',
    'portal.invalidLink': 'Недействительная ссылка',
    'portal.expiredLink': 'Ссылка истекла',
    'portal.orders': 'Заказы',
    'portal.payments': 'Платежи',
    'portal.totalOrders': 'Всего заказов',
    'portal.completedOrders': 'Выполнено',
    'portal.upcomingOrders': 'Предстоящие',
    'portal.totalPaid': 'Оплачено',
    'portal.totalBilled': 'Начислено',
    'portal.balanceDue': 'К оплате',
  },

  en: {
    // Navigation
    'nav.myDay': 'My Day',
    'nav.myOrders': 'My Jobs',
    'nav.calendar': 'Calendar',
    'nav.time': 'Timesheet',
    'nav.overview': 'Dashboard',
    'nav.orders': 'Work Orders',
    'nav.clients': 'Clients',
    'nav.workers': 'Crew',
    'nav.services': 'Services',
    'nav.reports': 'Reports',
    'nav.settings': 'Settings',
    'nav.integrations': 'Integrations',
    'nav.teamSchedule': 'Schedule',
    'nav.audit': 'Audit',
    'nav.payments': 'Payments',
    'nav.expenses': 'Expenses',
    'nav.chat': 'Internal Chat',

    // Chat
    'chat.title': 'Internal Chat',
    'chat.noConversations': 'No conversations',
    'chat.selectConversation': 'Select a conversation',
    'chat.startConversation': 'Send the first message',
    'chat.typePlaceholder': 'Type a message...',
    'chat.worker': 'Technician',
    'chat.manager': 'Manager',
    'chat.newChat': 'New Chat',
    'chat.selectRecipient': 'Select recipient',
    'chat.fromCRM': 'From CRM',
    'chat.fromDevice': 'From device',
    'chat.crmFiles': 'CRM Files',
    'chat.searchFiles': 'Search orders and clients...',
    'chat.invoices': 'Invoices',
    'chat.orderPhotos': 'Order Photos',
    'chat.orderDocs': 'Documents',
    'chat.sendToChat': 'Send to Chat',
    'chat.selectWorker': 'Select worker',
    'chat.sendFailed': 'Failed to send message',
    'chat.fileTooLarge': 'File too large (max 10MB)',

    // Roles
    'role.admin': 'Administrator',
    'role.manager': 'Manager',
    'role.worker': 'Technician',

    // Common
    'common.profile': 'Profile',
    'common.settings': 'Settings',
    'common.logout': 'Sign Out',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.create': 'Create',
    'common.search': 'Search',
    'common.loading': 'Loading...',
    'common.noData': 'No data',
    'common.today': 'Today',
    'common.week': 'Week',
    'common.back': 'Back',
    'common.close': 'Close',
    'common.confirm': 'Confirm',
    'common.date': 'Date',
    'common.navigation': 'Navigation',
    'common.error': 'Error',
    'common.mainMenu': 'Main application menu',
    'common.allClear': 'All clear!',
    'common.free': 'Free',
    'common.language': 'Language',
    'common.status': 'Status',
    'common.actions': 'Actions',
    'common.loadFailed': 'Failed to load data',
    'common.saveFailed': 'Failed to save',
    'common.saved': 'Saved',
    'common.exportFailed': 'Export failed',
    'common.testFailed': 'Test failed',

    // Currency
    'currency.symbol': '$',
    'currency.format': (v) => `$${Number(v).toLocaleString('en-US')}`,

    // Auth
    'auth.login': 'Sign In',
    'auth.email': 'Email address',
    'auth.password': 'Password',
    'auth.signIn': 'Sign in to your account',
    'auth.welcome': 'Welcome to CGC-CRM',

    // Dashboard - Worker
    'worker.hello': (name) => `Hi, ${name}!`,
    'worker.readyToStart': 'Ready to start?',
    'worker.haveAGoodDay': 'Have a great day!',
    'worker.startDay': 'Clock In',
    'worker.endDay': 'Clock Out',
    'worker.dayStarted': (time) => `Clocked in at ${time}`,
    'worker.dayEnded': 'Shift complete',
    'worker.dayStartedMsg': 'You\'re clocked in!',
    'worker.dayEndedMsg': 'Shift complete. Great work!',
    'worker.ordersCount': 'Jobs',
    'worker.inProgress': 'Active',
    'worker.completed': 'Done',
    'worker.currentlyActive': 'Currently Active',
    'worker.upcoming': (n) => `Upcoming (${n})`,
    'worker.completedSection': (n) => `Completed (${n})`,
    'worker.noOrdersToday': 'No jobs scheduled today',
    'worker.ordersWillAppear': 'Jobs will appear once assigned',
    'worker.startWork': 'Start Job',
    'worker.finishWork': 'Complete Job',
    'worker.workStarted': 'Job started!',
    'worker.workFinished': 'Job completed!',
    'worker.requiresService': 'Flag for Review',
    'worker.alreadyMarked': 'Already flagged',
    'worker.requiresServiceLabel': 'Additional service required',
    'worker.marked': 'Flagged',
    'worker.addNote': 'Note',
    'worker.addNotePlaceholder': 'Add a note...',
    'worker.noteAdded': 'Note saved',
    'worker.call': 'Call',
    'worker.sms': 'Text',
    'worker.route': 'Navigate',
    'worker.addressNotSet': 'No address on file',
    'worker.amount': 'Total',

    // Dashboard - Manager
    'manager.welcome': (name) => `Welcome back, ${name}!`,
    'manager.businessOverview': 'Here\'s your business overview for today',
    'manager.ordersToday': 'Jobs',
    'manager.todayLabel': 'Today',
    'manager.doneLabel': 'Completed',
    'manager.inProgressLabel': 'In Progress',
    'manager.workersLabel': 'Crew',
    'manager.quickActions': 'Quick Actions',
    'manager.newOrder': 'New Work Order',
    'manager.clientsAction': 'Clients',
    'manager.openCalendar': 'Open Calendar',
    'manager.needsAttention': 'Needs Attention',
    'manager.ordersInProgress': (n) => `${n} job${n !== 1 ? 's' : ''} in progress`,

    // Photos
    'photos.title': 'Photos',
    'photos.before': 'Before Photo',
    'photos.after': 'After Photo',
    'photos.beforeLabel': (n) => `Before (${n})`,
    'photos.afterLabel': (n) => `After (${n})`,
    'photos.otherLabel': (n) => `Other (${n})`,
    'photos.noPhotos': 'No photos yet. Tap above to add one.',
    'photos.uploaded': 'Photo uploaded!',
    'photos.uploadError': 'Upload failed',
    'photos.onlyImages': 'Images only',
    'photos.maxSize': '10 MB max',

    // Files
    'files.title': 'Files',
    'files.attach': 'Attach File',
    'files.uploaded': 'File uploaded!',
    'files.deleted': 'File removed',
    'files.uploadError': 'Upload failed',
    'files.deleteError': 'Could not remove file',
    'files.downloadError': 'Download failed',

    // Calendar
    'calendar.title': 'Calendar',
    'calendar.selectWorker': 'Select a worker to view schedule',
    'calendar.noOrders': 'No jobs scheduled for this day',
    'calendar.unscheduled': 'Unscheduled:',
    'calendar.orders': (n) => `${n} job${n !== 1 ? 's' : ''}`,
    'calendar.filterWorker': 'Filter by crew member',
    'calendar.allWorkers': 'All Crew',
    'calendar.teamSchedule': 'Team Schedule',
    'calendar.workload': 'Workload',
    'calendar.workloadLow': 'Light',
    'calendar.workloadMedium': 'Moderate',
    'calendar.workloadHigh': 'Heavy',
    'calendar.timeBlocks': 'Time Blocks',
    'calendar.timeBlocksDescription': 'Block time for workers',
    'calendar.activeBlocks': 'Active Blocks',
    'calendar.noBlocks': 'No active blocks',
    'calendar.addBlock': 'Add Block',
    'calendar.blockAdded': 'Block added',
    'calendar.blockDeleted': 'Block deleted',
    'calendar.blocked': 'Blocked',
    'calendar.blockTitle': 'E.g.: Vacation, Meeting',
    'calendar.reason': 'Reason',
    'calendar.blockReason': 'Enter reason (optional)',
    'calendar.filterActive': 'Worker Schedule',
    'calendar.addOrder': 'Add Order',
    'calendar.clickToAdd': 'Add order',
    'calendar.reassign': 'Reassign',
    'calendar.noWorkerAssigned': 'No worker assigned',
    'calendar.orderMoved': 'Order moved',
    'calendar.moveError': 'Move failed',
    'calendar.dragHint': 'Drag order to another day',
    'calendar.timeConflict': 'Time conflict',
    'calendar.dropBlocked': 'Drop blocked',

    // Common
    'common.clear': 'Clear',
    'common.save': 'Save',
    'common.cancel': 'Cancel',

    // Statuses
    'status.new': 'New',
    'status.confirmed': 'Confirmed',
    'status.in_progress': 'In Progress',
    'status.done': 'Completed',
    'status.cancelled': 'Cancelled',
    'status.deleted': 'Deleted',

    // Payment statuses
    'status.unpaid': 'Unpaid',
    'status.partial': 'Partial',
    'status.paid': 'Paid',
    'status.refund': 'Refund',
    
    // Status section titles
    'status.operationalTitle': 'Work Status',
    'status.paymentTitle': 'Payment Status',

    // Priority
    'priority.low': 'Low',
    'priority.normal': 'Normal',
    'priority.high': 'High',
    'priority.urgent': 'Urgent',

    // Order statuses - action
    'action.confirm': 'Confirm',
    'action.startWork': 'Start Job',
    'action.complete': 'Complete',
    'action.openOrder': 'View Details',

    // Services
    'services.title': 'Service Types',
    'services.newService': 'New Service',
    'services.name': 'Name',
    'services.description': 'Description',
    'services.price': 'Rate',
    'services.duration': 'Duration (min)',
    'services.color': 'Color',
    'services.active': 'Active',

    // Clients
    'clients.title': 'Clients',
    'clients.subtitle': 'Customer database',
    'clients.addClient': 'Add Client',
    'clients.editClient': 'Edit Client',
    'clients.name': 'Company / Name',
    'clients.namePlaceholder': 'Acme Corp or John Smith',
    'clients.phone': 'Phone',
    'clients.phoneSecondary': 'Alt. Phone',
    'clients.email': 'Email',
    'clients.address': 'Address',
    'clients.street': 'Street',
    'clients.city': 'City',
    'clients.postal': 'ZIP Code',
    'clients.tags': 'Tags (comma separated)',
    'clients.tagsPlaceholder': 'vip, cleaning, recurring',
    'clients.pinnedNote': 'Pinned Note (always visible)',
    'clients.pinnedNotePlaceholder': 'Important client info...',
    'clients.notes': 'Notes',
    'clients.notesPlaceholder': 'Additional information...',
    'clients.contacts': 'Contacts',
    'clients.primary': 'Primary',
    'clients.stats': 'Stats',
    'clients.ordersCount': 'Orders',
    'clients.balance': 'Balance',
    'clients.createOrder': 'Create Order',
    'clients.notFound': 'No clients found',
    'clients.noClients': 'No clients yet',
    'clients.addFirst': 'Add your first client',
    'clients.inactive': 'Inactive',
    'clients.deactivateConfirm': 'Are you sure you want to deactivate this client?',
    'clients.openCard': 'View Details',
    'clients.searchPlaceholder': 'Search by name, email, phone, address...',
    'clients.portalLinkCreated': 'Portal link created',
    'clients.portalLinkFailed': 'Failed to create link',
    'clients.linkCopied': 'Link copied',

    // Workers
    'workers.title': 'Crew Members',
    'workers.subtitle': 'Field team management',
    'workers.addWorker': 'Add Member',
    'workers.editWorker': 'Edit Member',
    'workers.firstName': 'First Name',
    'workers.lastName': 'Last Name',
    'workers.email': 'Email',
    'workers.password': 'Password',
    'workers.phone': 'Phone',
    'workers.color': 'Calendar Color',
    'workers.worker': 'Worker',
    'workers.skills': 'Skills (comma separated)',
    'workers.skillsPlaceholder': 'cleaning, window wash, carpet',
    'workers.hourlyRate': 'Hourly Rate',
    'workers.paymentType': 'Payment Type',
    'workers.hourlyPayment': 'Hourly',
    'workers.hourlyPaymentDesc': 'Payment per worked hours',
    'workers.percentagePayment': 'Percentage',
    'workers.percentagePaymentDesc': 'Percentage of revenue',
    'workers.percentageRate': 'Revenue Percentage',
    'workers.percentageNote': 'Salary = % of worker weekly revenue',
    'workers.percentageFromRevenue': 'of weekly revenue',
    'workers.noRateSet': 'Rate not set',
    'workers.active': 'Active',
    'workers.inactive': 'Inactive',
    'workers.noInactiveWorkers': 'No inactive workers',
    'workers.restore': 'Restore',
    'workers.activate': 'Activate',
    'workers.deactivate': 'Deactivate',
    'workers.activateConfirm': 'Activate this member?',
    'workers.deactivateConfirm': 'Deactivate this member?',
    'workers.noWorkers': 'No crew members',
    'workers.addFirst': 'Add your first team member',
    'workers.searchPlaceholder': 'Search crew members...',
    'workers.availability': 'Availability',
    'workers.currentSchedule': 'Current Schedule',
    'workers.noAvailability': 'No schedule configured',
    'workers.addAvailability': 'Add Time Slot',
    'workers.available': 'Available',
    'workers.unavailable': 'Unavailable',
    'workers.delete': 'Delete',
    'workers.deleteConfirm': 'Delete worker?',
    'workers.deleteWarning': (name) => `Are you sure you want to delete ${name}? This action cannot be undone.`,
    'workers.deactivated': 'Worker moved to inactive',
    'workers.restored': 'Worker restored',
    'workers.deactivateFailed': 'Failed to deactivate worker',
    'workers.restoreFailed': 'Failed to restore worker',
    'workers.updated': 'Worker updated successfully',
    'workers.updateFailed': 'Failed to update worker',
    'workers.editWorker': 'Edit Worker',
    'workers.editWorkerDesc': 'Update worker information',
    'workers.viewProfile': 'Worker Folder',
    'workers.cinNumber': 'CIN Number',
    'workers.homeAddress': 'Home Address',
    'workers.homeAddressPlaceholder': '123 Example St',
    'workers.workingDays': 'Working Days',
    'workers.saturday': 'Saturday',
    'workers.sunday': 'Sunday',
    'workers.weekendHint': 'Auto-scheduling will include weekends only for checked days',
    'workers.specialization': 'Specialization',
    'workers.specializationPlaceholder': 'E.g.: Electrician, Plumber',
    'workers.contactInfo': 'Contact Information',
    'workers.tapToCall': 'Tap to call',
    'workers.salary': 'Hourly Rate',
    'workers.credentials': 'Login Credentials',
    'workers.login': 'Login (Email)',
    'workers.password': 'Password',
    'workers.newPassword': 'New Password',
    'workers.enterNewPassword': 'Enter new password',
    'workers.passwordChanged': 'Password changed',
    'workers.passwordChangeFailed': 'Failed to change password',
    'workers.passwordTooShort': 'Password too short (min. 4 characters)',
    'workers.show': 'Show',
    'workers.hide': 'Hide',
    'workers.change': 'Change',
    'workers.passwordNote': 'Password was set when creating the worker',
    'workers.leaveBlankToKeep': 'Leave blank to keep current password',
    'workers.role': 'Role',
    'workers.roleAdmin': 'Admin',
    'workers.roleWorker': 'Worker',
    'workers.roleWorkerDesc': 'Performs orders',
    'workers.roleManager': 'Manager',
    'workers.roleManagerDesc': 'Manages orders and clients',
    'workers.files': 'Files & Documents',
    'workers.uploadFile': 'Upload File',
    'workers.noFiles': 'No files uploaded',
    'workers.fileUploaded': 'File uploaded',
    'workers.fileDeleted': 'File deleted',
    'workers.fileUploadError': 'Failed to upload file',
    'workers.fileDeleteError': 'Failed to delete file',

    // Common - additions
    'common.day': 'Day',
    'common.startTime': 'Start Time',
    'common.endTime': 'End Time',
    'common.add': 'Add',
    'common.all': 'All',
    'common.from': 'From',
    'common.to': 'To',
    'common.filter': 'Filter',
    'common.actions': 'Actions',

    // Login
    'login.title': 'Sign In',
    'login.subtitle': 'Enter your credentials',
    'login.email': 'Email Address',
    'login.emailOrUsername': 'Login or Email',
    'login.emailOrUsernamePlaceholder': 'Enter login or email',
    'login.password': 'Password',
    'login.signIn': 'Sign In',
    'login.invalidCredentials': 'Invalid login or password. Please try again.',
    'login.welcome': 'Welcome to CGC-CRM',
    'login.description': 'Field Service Management Platform',

    // Orders
    'orders.title': 'Work Orders',
    'orders.subtitle': 'Manage orders and assignments',
    'orders.newOrder': 'New Work Order',
    'orders.client': 'Client',
    'orders.assignedTo': 'Assigned to:',
    'orders.noDate': 'No date specified',
    'orders.updated': 'Order updated',
    'orders.updateFailed': 'Failed to update order',
    'orders.deleted': 'Order deleted',
    'orders.deleteFailed': 'Failed to delete order',
    'orders.cannotDeleteCompleted': 'Cannot delete completed order',
    'orders.deleteConfirm': (title) => `Delete order "${title}"?`,
    'orders.searchPlaceholder': 'Search by title...',
    'orders.allStatuses': 'All Statuses',
    'orders.allPayments': 'All Payments',
    'orders.notFound': 'Orders not found',
    'orders.noOrders': 'No orders',
    'orders.date': 'Date',
    'orders.workers': 'Workers',
    'orders.total': 'Total',
    'orders.paid': 'Paid',
    'orders.orderTotal': 'Order Total',
    'orders.notAssigned': 'not assigned',
    'orders.serviceRequired': 'Service Required',

    // Notifications
    'notifications.title': 'Notifications',
    'notifications.markAllRead': 'Mark All Read',
    'notifications.noNew': 'No new notifications',

    // Weekdays
    'weekday.mon': 'Mon',
    'weekday.tue': 'Tue',
    'weekday.wed': 'Wed',
    'weekday.thu': 'Thu',
    'weekday.fri': 'Fri',
    'weekday.sat': 'Sat',
    'weekday.sun': 'Sun',
    'weekday.monday': 'Monday',
    'weekday.tuesday': 'Tuesday',
    'weekday.wednesday': 'Wednesday',
    'weekday.thursday': 'Thursday',
    'weekday.friday': 'Friday',
    'weekday.saturday': 'Saturday',
    'weekday.sunday': 'Sunday',

    // Time Tracking
    'time.title': 'Timesheet',
    'time.pageTitle': 'Time Approval',
    'time.pageSubtitle': 'Review and approve crew timesheets',
    'time.myTime': 'My Timesheet',
    'time.myTimeSubtitle': 'Your work hours history',
    'time.pending': 'Pending',
    'time.approved': 'Approved',
    'time.rejected': 'Rejected',
    'time.all': 'All',
    'time.hours': 'h',
    'time.hoursLabel': 'Hours',
    'time.date': 'Date',
    'time.worker': 'Crew Member',
    'time.dayStart': 'Clock In',
    'time.dayEnd': 'Clock Out',
    'time.totalHours': 'Hours',
    'time.ordersCompleted': 'Jobs',
    'time.status': 'Status',
    'time.actions': 'Actions',
    'time.approve': 'Approve',
    'time.reject': 'Reject',
    'time.approveConfirm': 'Approve this timesheet?',
    'time.rejectReason': 'Rejection reason',
    'time.rejectReasonPlaceholder': 'Enter reason for rejection...',
    'time.approvedMsg': 'Timesheet approved',
    'time.rejectedMsg': 'Timesheet rejected',
    'time.noSubmissions': 'No timesheets to review',
    'time.summary': 'Period Summary',
    'time.summaryWorker': (name) => `Summary: ${name}`,
    'time.periodStart': 'From',
    'time.periodEnd': 'To',
    'time.approvedHours': 'Approved',
    'time.pendingHours': 'Pending',
    'time.rejectedHours': 'Rejected',
    'time.totalDays': 'Days',
    'time.reviewedBy': 'Reviewed by',
    'time.reviewedAt': 'Review date',
    'time.rejectionReason': 'Reason',
    'time.export': 'Export',
    'time.exportCSV': 'Download CSV',
    'time.exportPDF': 'Download PDF',
    'time.exportSuccess': 'Report exported',
    'time.auditHistory': 'Audit History',
    'time.auditHistoryDescription': 'History of all time status changes',
    'time.noAuditHistory': 'No audit records',
    'time.action.approve': 'Approved',
    'time.action.reject': 'Rejected',
    'time.action.modify': 'Modified',
    'time.action.resubmit': 'Resubmitted',
    'time.by': 'By',

    // Payroll
    'payroll.title': 'Payroll',
    'payroll.subtitle': 'Crew payment calculations',
    'payroll.newRun': 'New Payroll Run',
    'payroll.periodStart': 'Period Start',
    'payroll.periodEnd': 'Period End',
    'payroll.createRun': 'Create Run',
    'payroll.draft': 'Draft',
    'payroll.approved': 'Approved',
    'payroll.paid': 'Paid',
    'payroll.totalAmount': 'Total Payable',
    'payroll.worker': 'Crew Member',
    'payroll.paymentType': 'Pay Type',
    'payroll.hourly': 'Hourly',
    'payroll.fixed': 'Fixed Salary',
    'payroll.perOrder': 'Per Job',
    'payroll.combined': 'Combined',
    'payroll.hoursWorked': 'Hours',
    'payroll.hourlyRate': 'Rate/hr',
    'payroll.ordersCompleted': 'Jobs',
    'payroll.perOrderRate': 'Per Job',
    'payroll.fixedSalary': 'Fixed',
    'payroll.bonus': 'Bonus',
    'payroll.penalty': 'Penalty',
    'payroll.gross': 'Gross',
    'payroll.net': 'Net Pay',
    'payroll.addBonus': 'Add Bonus/Penalty',
    'payroll.bonusNote': 'Bonus reason',
    'payroll.penaltyNote': 'Penalty reason',
    'payroll.approve': 'Approve',
    'payroll.markPaid': 'Mark as Paid',
    'payroll.delete': 'Delete',
    'payroll.export': 'Export CSV',
    'payroll.noRuns': 'No payroll runs',
    'payroll.createFirst': 'Create your first payroll run',
    'payroll.runCreated': 'Payroll run created',
    'payroll.runApproved': 'Payroll run approved',
    'payroll.runPaid': 'Payroll run marked as paid',
    'payroll.runDeleted': 'Payroll run deleted',
    'payroll.bonusAdded': 'Bonus/penalty added',

    // Reports
    'reports.title': 'Reports',
    'reports.subtitle': 'Analytics and insights',
    'reports.period': 'Period',
    'reports.overview': 'Overview',
    'reports.orders': 'Orders',
    'reports.revenue': 'Revenue',
    'reports.workers': 'Crew',
    'reports.services': 'Services',
    'reports.totalOrders': 'Total Orders',
    'reports.completedOrders': 'Completed',
    'reports.cancelledOrders': 'Cancelled',
    'reports.completionRate': 'Completion',
    'reports.totalRevenue': 'Revenue',
    'reports.avgOrderValue': 'Avg Order',
    'reports.paidOrders': 'Paid',
    'reports.newClients': 'New Clients',
    'reports.activeWorkers': 'Active Crew',
    'reports.totalHours': 'Hours',
    'reports.workerName': 'Crew Member',
    'reports.ordersCompleted': 'Completed',
    'reports.revenuePerHour': '$/hr',
    'reports.hoursWorked': 'Hours',
    'reports.serviceName': 'Service',
    'reports.ordersCount': 'Orders',
    'reports.export': 'Export CSV',
    'reports.noData': 'No data for this period',
    'reports.byDate': 'By Date',
    'reports.byWorker': 'By Crew',
    'reports.byService': 'By Service',
    'reports.byStatus': 'By Status',
    'reports.workload': 'Workload',
    'reports.workloadComparison': 'Workload Comparison',
    'reports.scheduledHours': 'Scheduled',
    'reports.workedHours': 'Worked',
    'reports.availableCapacity': 'Available',
    'reports.utilization': 'Utilization',
    'reports.efficiency': 'Efficiency',
    'reports.hours': 'hours',
    'reports.exportPDF': 'Export PDF',
    'reports.payroll': 'Payroll',
    'reports.payrollTitle': 'Payroll Report',
    'reports.paymentType': 'Pay Type',
    'reports.rate': 'Rate',
    'reports.hoursOrOrders': 'Hours/Jobs',
    'reports.ordersRevenue': 'Revenue',
    'reports.salary': 'Salary',
    'reports.totalSalary': 'Total Payable',
    'reports.workersCount': 'Crew Count',
    'reports.ordersShort': 'jobs',
    'reports.totalPayroll': 'Total Payroll',

    // Scheduling
    'schedule.title': 'Schedule',
    'schedule.conflicts': 'Conflicts',
    'schedule.noConflicts': 'No conflicts',
    'schedule.hasConflicts': 'Conflicts detected',
    'schedule.conflictOverlap': 'Overlaps with another order',
    'schedule.conflictUnavailable': 'Worker unavailable',
    'schedule.conflictBlocked': 'Time blocked',
    'schedule.availability': 'Availability',
    'schedule.setAvailability': 'Set availability',
    'schedule.available': 'Available',
    'schedule.unavailable': 'Unavailable',
    'schedule.vacation': 'Vacation',
    'schedule.sick': 'Sick leave',
    'schedule.training': 'Training',
    'schedule.blocks': 'Time blocks',
    'schedule.addBlock': 'Add block',
    'schedule.lunch': 'Lunch',
    'schedule.meeting': 'Meeting',
    'schedule.personal': 'Personal',
    'schedule.travel': 'Travel',
    'schedule.buffer': 'Buffer between orders',
    'schedule.bufferMinutes': 'minutes',
    'schedule.settings': 'Schedule settings',
    'schedule.workDayStart': 'Work day start',
    'schedule.workDayEnd': 'Work day end',

    // Audit
    'audit.title': 'Audit Log',
    'audit.subtitle': 'History of all system actions',
    'audit.accessDenied': 'Access denied',
    'audit.totalEvents': 'Total Events',
    'audit.created': 'Created',
    'audit.updated': 'Updated',
    'audit.activeUsers': 'Active Users',
    'audit.entityType': 'Entity Type',
    'audit.action': 'Action',
    'audit.events': 'Events',
    'audit.noEvents': 'No events for selected period',
    'audit.timestamp': 'Timestamp',
    'audit.user': 'User',
    'audit.entity': 'Entity',
    'audit.eventDetails': 'Event Details',
    'audit.entityName': 'Name',
    'audit.details': 'Details',
    'audit.oldValue': 'Old Value',
    'audit.newValue': 'New Value',
    'audit.entity.order': 'Order',
    'audit.entity.client': 'Client',
    'audit.entity.worker': 'Worker',
    'audit.entity.user': 'User',
    'audit.entity.time': 'Time',
    'audit.entity.time_submission': 'Time',
    'audit.entity.document': 'Document',
    'audit.entity.payroll': 'Payroll',
    'audit.entity.scheduling': 'Scheduling',
    'audit.action.create': 'Create',
    'audit.action.update': 'Update',
    'audit.action.delete': 'Delete',
    'audit.action.status': 'Status Change',
    'audit.action.status_change': 'Status Change',
    'audit.action.login': 'Login',
    'audit.action.logout': 'Logout',
    'audit.action.assign': 'Assign',
    'audit.action.approve': 'Approve',
    'audit.action.reject': 'Reject',
    'audit.action.export': 'Export',
    'audit.action.rate_change': 'Rate Change',

    // Notification Settings
    'notifications.settingsTitle': 'Notification Settings',
    'notifications.settingsSubtitle': 'Manage notification channels',
    'notifications.emailNotifications': 'Email Notifications',
    'notifications.emailDescription': 'Receive notifications via email',
    'notifications.emailEnabled': 'Enable email',
    'notifications.emailEnabledDesc': 'Receive notifications to your email',
    'notifications.onAssignment': 'On assignment',
    'notifications.onAssignmentDesc': 'When you are assigned to an order',
    'notifications.onStatusChange': 'On status change',
    'notifications.onStatusChangeDesc': 'When your order status changes',
    'notifications.onTimeApproval': 'On time approval',
    'notifications.onTimeApprovalDesc': 'When your time is approved or rejected',
    'notifications.pushNotifications': 'Push Notifications',
    'notifications.pushDescription': 'Instant notifications in browser',
    'notifications.pushEnabled': 'Push enabled',
    'notifications.pushEnabledDesc': 'Receive push notifications',
    'notifications.enablePush': 'Enable',
    'notifications.pushNotSupported': 'Push notifications not supported',
    'notifications.pushDenied': 'Notification permission denied',
    'notifications.pushFailed': 'Failed to enable push notifications',

    // Settings
    'settings.saved': 'Settings saved',
    'settings.saveFailed': 'Failed to save',

    // Payments
    'payments.title': 'Payments',
    'payments.subtitle': 'Manage payments and invoices',
    'payments.accessDenied': 'Access denied',
    'payments.createPayment': 'Create Payment',
    'payments.createDescription': 'Create a payment intent for an order',
    'payments.selectOrder': 'Select Order',
    'payments.selectOrderPlaceholder': 'Select an order...',
    'payments.amount': 'Amount',
    'payments.provider': 'Provider',
    'payments.manual': 'Manual',
    'payments.create': 'Create',
    'payments.fillAllFields': 'Fill all fields',
    'payments.created': 'Payment created',
    'payments.createFailed': 'Failed to create payment',
    'payments.configured': 'Configured',
    'payments.notConfigured': 'Not configured',
    'payments.setupRequired': 'Payment setup required',
    'payments.setupInstructions': 'Add API keys to .env file on server',
    'payments.stripeInstructions': 'STRIPE_SECRET_KEY and STRIPE_PUBLISHABLE_KEY',
    'payments.yookassaInstructions': 'YOOKASSA_SHOP_ID and YOOKASSA_SECRET_KEY',
    'payments.history': 'Payment History',
    'payments.noPayments': 'No payments',
    'payments.date': 'Date',
    'payments.order': 'Order',
    'payments.markPaid': 'Mark Paid',
    'payments.markedPaid': 'Payment marked as paid',
    'payments.markPaidFailed': 'Failed to mark payment',
    'payments.status.pending': 'Pending',
    'payments.status.succeeded': 'Paid',
    'payments.status.failed': 'Failed',
    'payments.status.refunded': 'Refunded',
    'payments.orderAmount': 'Order Amount',
    'payments.paid': 'Paid',
    'payments.paidInFull': 'Paid in full',
    'payments.remaining': 'Remaining',
    'payments.pay': 'Pay',
    'payments.pendingPayments': 'Pending payments',
    'payments.more': 'more',
    'payments.enterAmount': 'Enter amount',
    'payments.providerNotConfigured': 'Provider not configured. Use manual payment.',
    'payments.emailReceipts': 'Email Receipts',
    'payments.emailSetup': 'Email for receipts',

    // Reminders
    'reminders.sendReminder': 'Send Reminder',
    'reminders.remind': 'Remind',
    'reminders.type': 'Reminder Type',
    'reminders.type.payment': 'Payment Reminder',
    'reminders.type.appointment': 'Appointment Reminder',
    'reminders.type.custom': 'Custom Message',
    'reminders.channel': 'Channel',
    'reminders.both': 'SMS & Email',
    'reminders.noPhone': 'no phone',
    'reminders.noEmail': 'no email',
    'reminders.customMessage': 'Message Text',
    'reminders.customPlaceholder': 'Enter reminder text...',
    'reminders.characters': 'characters',
    'reminders.preview': 'Preview',
    'reminders.send': 'Send',
    'reminders.sent': 'Reminder sent',
    'reminders.failed': 'Failed to send',
    'reminders.sentSuccess': 'Reminder sent successfully!',
    'reminders.sentFailed': 'Failed to send reminder',

    // Client Portal
    'portal.title': 'Client Portal',
    'portal.generateLink': 'Generate Portal Link',
    'portal.copyLink': 'Copy Link',
    'portal.linkCopied': 'Link copied',
    'portal.linkGenerated': 'Link generated',
    'portal.invalidLink': 'Invalid link',
    'portal.expiredLink': 'Link expired',
    'portal.orders': 'Orders',
    'portal.payments': 'Payments',
    'portal.totalOrders': 'Total Orders',
    'portal.completedOrders': 'Completed',
    'portal.upcomingOrders': 'Upcoming',
    'portal.totalPaid': 'Total Paid',
    'portal.totalBilled': 'Total Billed',
    'portal.balanceDue': 'Balance Due',
  },
};

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem('cgc_lang') || 'ru');

  const toggleLanguage = useCallback(() => {
    setLang(prev => {
      const next = prev === 'ru' ? 'en' : 'ru';
      localStorage.setItem('cgc_lang', next);
      return next;
    });
  }, []);

  const setLanguage = useCallback((newLang) => {
    setLang(newLang);
    localStorage.setItem('cgc_lang', newLang);
  }, []);

  const t = useCallback((key, ...args) => {
    const val = translations[lang]?.[key] || translations.ru[key] || key;
    if (typeof val === 'function') return val(...args);
    return val;
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}
